/**
 * Growth-stage prompts are static. A stage is shown only when one of its
 * source groups is actually connected (plan 4 §4.4).
 *
 * Firebase, Mixpanel and GA4 are analytics. RevenueCat and the stores are
 * revenue. GitHub is code.
 */

export const PROVIDER_GROUPS = {
  analytics: ['firebase', 'mixpanel', 'ga4', 'google-analytics', 'google_analytics'],
  revenue: ['revenuecat', 'revenue-cat', 'app_store', 'app-store', 'app_store_connect', 'google_play', 'google-play', 'play_store'],
  code: ['github'],
};

export const STAGES = [
  {
    id: 'acquisition',
    name: 'Acquisition',
    hint: 'Where new users come from.',
    groups: ['analytics'],
    width: '100%',
    placeholder: 'Ask about installs and where new users come from…',
    questions: [
      'New installs, this week vs last',
      'Top install sources, last 30 days',
      'Which store listing change moved installs?',
    ],
  },
  {
    id: 'activation',
    name: 'Activation',
    hint: 'Who reaches their first key action.',
    groups: ['analytics'],
    width: '72%',
    placeholder: 'Ask where new users drop before their first key action…',
    questions: [
      'Install → first key action funnel',
      'Where do new users drop in onboarding?',
      'Activation by app version',
    ],
  },
  {
    id: 'retention',
    name: 'Retention',
    hint: 'Who keeps coming back.',
    groups: ['analytics', 'code'],
    width: '46%',
    placeholder: 'Ask about cohorts, D1, D7 and D30…',
    questions: [
      'Cohort retention, last 8 weeks',
      'What do retained users do in week one?',
      'Did the last release move D7?',
    ],
  },
  {
    id: 'monetisation',
    name: 'Monetisation',
    hint: 'Who pays, and for what.',
    groups: ['revenue'],
    width: '24%',
    placeholder: 'Ask about trials, paywalls and revenue…',
    questions: [
      'Trial → paid, last 30 days',
      'Paywall views vs purchases',
      'Revenue by plan this month',
    ],
  },
];

export const SUGGESTED = [
  { sources: [], label: 'All sources', question: 'Run the daily health check.' },
  { sources: ['analytics'], label: 'Analytics', question: 'How’s the paywall funnel this week?' },
  { sources: ['code'], label: 'Code', question: 'What shipped in the recent commits?' },
  { sources: ['revenue'], label: 'Revenue', question: 'Paid conversions, last 7 days.' },
];

function slug(integration) {
  return String(
    (integration && (integration.provider_name || integration.provider || integration.name)) || ''
  ).toLowerCase().replace(/\s+/g, '-');
}

/** @param {object} integration @returns {'analytics'|'revenue'|'code'|null} */
export function providerGroup(integration) {
  const name = slug(integration);
  for (const [group, names] of Object.entries(PROVIDER_GROUPS)) {
    if (names.some((item) => name === item || name.includes(item))) return group;
  }
  const depth = String((integration && integration.depth_category) || '').toLowerCase();
  if (depth === 'analytics' || depth === 'revenue' || depth === 'code') return depth;
  return null;
}

/**
 * @param {object[]} integrations
 * @returns {{ groups: Set<string>, labels: Record<string, string[]> }}
 */
export function connectedGroups(integrations) {
  const groups = new Set();
  const labels = { analytics: [], revenue: [], code: [] };
  for (const integration of integrations || []) {
    const group = providerGroup(integration);
    if (!group) continue;
    groups.add(group);
    const label = integration.provider_display || integration.display_name || integration.provider_name || group;
    if (label && !labels[group].includes(label)) labels[group].push(label);
  }
  return { groups, labels };
}

/** @param {typeof STAGES} stages @param {Set<string>|string[]} groups */
export function filterStages(stages, groups) {
  const set = groups instanceof Set ? groups : new Set(groups || []);
  return (stages || []).filter((stage) => (stage.groups || []).some((group) => set.has(group)));
}

/** Empty-source prompts stay only when something is connected. */
export function filterSuggested(items, groups) {
  const set = groups instanceof Set ? groups : new Set(groups || []);
  if (!set.size) return [];
  return (items || []).filter((item) => !item.sources.length || item.sources.some((group) => set.has(group)));
}

/** Labels of the connected providers that feed a stage, in group order. */
export function stageSources(stage, labels) {
  const names = [];
  for (const group of stage.groups || []) {
    for (const label of (labels && labels[group]) || []) {
      if (!names.includes(label)) names.push(label);
    }
  }
  return names.join(' · ');
}
